import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Clock, Handshake, Send, MessageCircle } from "lucide-react";
import { SectionHead } from "../components/SectionHead";
import { Reveal } from "../lib/motion";
import { REASONS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full bg-cream border border-ink/15 px-4 py-3.5 font-sans text-ink placeholder-slate/50 focus:border-gold transition-colors duration-200 outline-none";
const labelCls = "font-mono text-xs uppercase tracking-[0.14em] text-slate mb-2 block";

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", reason: "health-check", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Contact — PROSPEC Consultancy";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your enquiry has been sent. We'll respond within one working day.");
      setForm({ name: "", company: "", email: "", phone: "", reason: "health-check", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email us directly at info@prospecconsultancy.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <SectionHead
          eyebrow="Contact"
          title="Book your free programme health-check."
          intro="Tell us about your project. We'll come back within one working day with honest, specific next steps — no obligation."
        />

        <div className="grid lg:grid-cols-12 gap-12 mt-16">
          {/* FORM */}
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="c-name">Name *</label>
                  <input id="c-name" data-testid="contact-name" className={field} value={form.name} onChange={set("name")} placeholder="Your full name" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="c-company">Company</label>
                  <input id="c-company" data-testid="contact-company" className={field} value={form.company} onChange={set("company")} placeholder="Your company" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="c-email">Email *</label>
                  <input id="c-email" type="email" data-testid="contact-email" className={field} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="c-phone">Phone</label>
                  <input id="c-phone" data-testid="contact-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="Optional" />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="c-reason">Reason for contact</label>
                <select id="c-reason" data-testid="contact-reason" className={field} value={form.reason} onChange={set("reason")}>
                  {REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="c-message">Message *</label>
                <textarea id="c-message" rows={5} data-testid="contact-message" className={field} value={form.message} onChange={set("message")} placeholder="Tell us about your project and where your programme stands today." />
              </div>
              <motion.button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                className="btn-sheen inline-flex items-center gap-2 bg-gold text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
              >
                {loading ? "Sending…" : <>Send enquiry <Send size={15} /></>}
              </motion.button>
            </form>
          </Reveal>

          {/* INFO */}
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="grain relative overflow-hidden bg-ink text-cream p-10 h-full">
              <div className="relative">
                <p className="eyebrow mb-8">Direct contact</p>
                <div className="space-y-8">
                  <InfoRow icon={Mail} label="Email us" value="info@prospecconsultancy.com" href="mailto:info@prospecconsultancy.com" />
                  <InfoRow icon={MessageCircle} label="WhatsApp" value="+44 7827 229323" href="https://wa.me/447827229323" />
                  <InfoRow icon={Clock} label="Response time" value="Within one working day" />
                  <InfoRow icon={Handshake} label="Engagement type" value="Monthly retainer — outsourced planning & project controls" />
                </div>
                <div className="mt-12 pt-8 border-t border-cream/10">
                  <p className="text-cream/60 leading-relaxed text-sm">
                    We work with contractors across the construction, civil engineering, transport,
                    production and infrastructure sectors — not limited to any one region.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value, href }) => (
  <div className="flex gap-4">
    <div className="w-10 h-10 border border-cream/20 flex items-center justify-center shrink-0">
      <Icon size={18} className="text-gold" />
    </div>
    <div>
      <p className="font-mono text-xs uppercase tracking-[0.14em] text-cream/50 mb-1">{label}</p>
      {href ? (
        <a href={href} className="font-display text-lg text-cream hover:text-gold transition-colors break-all">{value}</a>
      ) : (
        <p className="font-display text-lg text-cream leading-snug">{value}</p>
      )}
    </div>
  </div>
);
